import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

interface Props {
  total: number
  offset: number
  take: number
  onOffsetChange: (offset: number) => void
  onTakeChange: (take: number) => void
  /** The table (or mobile+desktop pair) this pager belongs to. */
  children: ReactNode
  /**
   * Right-side table actions (report, invite, backup register, …). Always shown, even when
   * the pager itself is hidden because everything fits on one page.
   */
  actions?: ReactNode
  /** Extra left-side content after the pager — typically bulk selection actions. */
  selectionActions?: ReactNode
  /** Optional test id on the combined toolbar row. */
  toolbarTestId?: string
}

/**
 * Toolbar + pager for one Event Management participants table (see `usePagedParticipants`).
 * One row above the table: Previous/Next, page size, and the "Showing …" comment on the left;
 * table actions (invite, reports, …) on the right. The pager controls themselves are hidden
 * when everything already fits on one page (`total <= take`).
 */
export function TablePagination({
  total,
  offset,
  take,
  onOffsetChange,
  onTakeChange,
  children,
  actions,
  selectionActions,
  toolbarTestId,
}: Props) {
  const { t } = useTranslation()

  const showPager = total > take
  const showBar = showPager || Boolean(actions) || Boolean(selectionActions)

  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + take, total)
  const hasPrevious = offset > 0
  const hasNext = offset + take < total

  return (
    <div className="flex flex-col gap-2">
      {showBar && (
        <div
          data-testid={toolbarTestId}
          className="flex flex-wrap items-center justify-between gap-3 px-1 py-2"
        >
          <div className="flex flex-wrap items-center gap-3">
            {showPager && (
              <>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!hasPrevious}
                    onClick={() => onOffsetChange(Math.max(0, offset - take))}
                  >
                    {t('btn_previous')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => onOffsetChange(offset + take)}
                  >
                    {t('btn_next')}
                  </Button>
                </div>

                <label className="text-pax-gray flex items-center gap-1.5 text-xs">
                  <span>{t('table_page_size_label')}</span>
                  <select
                    value={take}
                    onChange={(e) => onTakeChange(Number(e.target.value))}
                    className="border-pax-purple/30 rounded-lg border px-2 py-1 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>

                <p className="text-pax-gray text-xs">{t('table_showing', { from, to, total })}</p>
              </>
            )}
            {selectionActions}
          </div>

          {actions != null && (
            <div
              data-testid={toolbarTestId ? `${toolbarTestId}-actions` : undefined}
              className="flex flex-wrap items-center justify-end gap-2"
            >
              {actions}
            </div>
          )}
        </div>
      )}

      {children}
    </div>
  )
}
